import { API, expressAPI } from 'src/utils/interceptor'
import {
  FOOD_SAFETY_CAPABILITIES_HEADER,
  FOOD_SAFETY_CLIENT_CAPABILITIES,
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_PERMISSION_DENIED_CODE,
} from 'src/constants/foodSafety'
import { notifyFoodSafetyChanged } from 'src/hooks/useFoodSafetyTodayBadgeCount'
import {
  OLLIE_FINDINGS_WRITE_EVENTS,
  ollieFindingsSaved,
} from 'src/utils/ollieFindingsRefresh'

const foodSafetyBase = venueId => `venue/${venueId}/food-safety`

/** Request options with the Food Safety capability header (every call). */
export const withFoodSafetyCapabilities = (options = {}) => ({
  ...options,
  headers: {
    ...(options.headers || {}),
    [FOOD_SAFETY_CAPABILITIES_HEADER]: FOOD_SAFETY_CLIENT_CAPABILITIES,
  },
})

/**
 * @param {unknown} error
 * @returns {{ locked: boolean, message: string, data: object|null }}
 */
export function readFoodSafetyHistoryLock(error) {
  const data = error?.response?.data
  if (
    error?.response?.status === 403 &&
    (data?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE ||
      data?.data?.upgradeHint ||
      /14 days|Starter/i.test(data?.message || ''))
  ) {
    return {
      locked: true,
      message:
        data?.message ||
        'Food Safety history older than 14 days is available on Starter.',
      data: data?.data || null,
    }
  }
  return { locked: false, message: '', data: null }
}

/**
 * Home requests already on the wire, by venue + day. The nav badge and Home
 * ask on the same beat (mount, poll, focus, after a save) — they share one.
 */
const homeInFlight = new Map()

/** After a write, the next Home load must not reuse a request from before it. */
export const forgetFoodSafetyHomeInFlight = () => homeInFlight.clear()

/**
 * Today’s oversight home (section statuses + historyAccess).
 * @param {string} venueId
 * @param {{ date?: string, apiGet?: Function }} [options]
 */
export function fetchFoodSafetyHome(venueId, { date, apiGet = API.get } = {}) {
  if (!venueId) return Promise.reject(new Error('venueId is required'))
  const key = `${venueId}|${date || ''}`
  const shared = apiGet === API.get ? homeInFlight.get(key) : null
  if (shared) return shared
  const request = loadFoodSafetyHome(venueId, { date, apiGet })
  if (apiGet === API.get) {
    homeInFlight.set(key, request)
    const forget = () => {
      if (homeInFlight.get(key) === request) homeInFlight.delete(key)
    }
    request.then(forget).catch(forget)
  }
  return request
}

async function loadFoodSafetyHome(venueId, { date, apiGet }) {
  try {
    const response = await apiGet(
      'api2',
      `${foodSafetyBase(venueId)}/home`,
      withFoodSafetyCapabilities({
        queryStringParameters: date ? { date } : undefined,
      }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to load Food Safety home')
    }
    return response.data
  } catch (error) {
    const lock = readFoodSafetyHistoryLock(error)
    if (lock.locked) {
      const err = new Error(lock.message)
      err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
      err.historyAccess = lock.data
      err.statusCode = 403
      throw err
    }
    throw error
  }
}

/**
 * Section day detail (definitions + expectations + logs).
 * @param {string} venueId
 * @param {{ date?: string, section: string, apiGet?: Function }} options
 */
export async function fetchFoodSafetyDay(
  venueId,
  { date, section, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!section) throw new Error('section is required')

  try {
    const response = await apiGet(
      'api2',
      `${foodSafetyBase(venueId)}/day`,
      withFoodSafetyCapabilities({
        queryStringParameters: {
          section,
          ...(date ? { date } : {}),
        },
      }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to load Food Safety day')
    }
    return response.data
  } catch (error) {
    const lock = readFoodSafetyHistoryLock(error)
    if (lock.locked) {
      const err = new Error(lock.message)
      err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
      err.historyAccess = lock.data
      err.statusCode = 403
      throw err
    }
    throw error
  }
}

/**
 * Submit a Food Safety log (server stamps user / time / venue). With a
 * `submissionKey`, a retry of a save that already landed returns that first
 * reading (`replayed: true`) instead of saving a second.
 * @param {string} venueId
 * @param {object} body
 * @param {{ apiPost?: Function, submissionKey?: string }} [options]
 */
export async function createFoodSafetyLog(
  venueId,
  body,
  { apiPost = API.post, submissionKey } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const response = await apiPost(
    'api2',
    `${foodSafetyBase(venueId)}/logs`,
    withFoodSafetyCapabilities({
      body: submissionKey ? { ...body, submissionKey } : body,
    }),
  )
  if (!response?.success) {
    const err = new Error(response?.message || 'Failed to save Food Safety log')
    err.statusCode = 400
    throw err
  }
  notifyFoodSafetyChanged()
  ollieFindingsSaved({
    venueId,
    event: OLLIE_FINDINGS_WRITE_EVENTS.FOOD_SAFETY_LOGGED,
  })
  return response.data
}

/**
 * Undo a same-day Skip today log (29.28).
 * @param {string} venueId
 * @param {string} logId
 * @param {{ apiPost?: Function }} [options]
 */
export async function finishFoodSafetyLog(
  venueId,
  logId,
  body,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!logId) throw new Error('logId is required')
  const response = await apiPost(
    'api2',
    `${foodSafetyBase(venueId)}/logs/${logId}/finish`,
    withFoodSafetyCapabilities({ body: body || {} }),
  )
  if (!response?.success) {
    const err = new Error(
      response?.message || 'Failed to finish this cooling batch',
    )
    err.statusCode = 400
    throw err
  }
  notifyFoodSafetyChanged()
  ollieFindingsSaved({
    venueId,
    event: OLLIE_FINDINGS_WRITE_EVENTS.FOOD_SAFETY_LOGGED,
  })
  return response.data
}

export async function undoFoodSafetySkipLog(
  venueId,
  logId,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!logId) throw new Error('logId is required')
  const response = await apiPost(
    'api2',
    `${foodSafetyBase(venueId)}/logs/${logId}/undo-skip`,
    withFoodSafetyCapabilities({ body: {} }),
  )
  if (!response?.success) {
    const err = new Error(response?.message || 'Failed to undo Skip today')
    err.statusCode = 400
    throw err
  }
  notifyFoodSafetyChanged()
  ollieFindingsSaved({
    venueId,
    event: OLLIE_FINDINGS_WRITE_EVENTS.FOOD_SAFETY_LOGGED,
  })
  return response.data
}

/**
 * Replace the temperature that is shown for a saved reading.
 * The first submitted number stays on the record.
 */
export async function correctFoodSafetyLog(
  venueId,
  logId,
  { value, note } = {},
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!logId) throw new Error('logId is required')
  const response = await apiPost(
    'api2',
    `${foodSafetyBase(venueId)}/logs/${logId}/corrections`,
    withFoodSafetyCapabilities({
      body: { value, note: note || '' },
    }),
  )
  if (!response?.success) {
    const err = new Error(response?.message || 'Failed to correct this reading')
    err.statusCode = 400
    throw err
  }
  notifyFoodSafetyChanged()
  ollieFindingsSaved({
    venueId,
    event: OLLIE_FINDINGS_WRITE_EVENTS.FOOD_SAFETY_LOGGED,
  })
  return response.data
}

/**
 * List definitions (manage). includeInactive for archive review.
 */
export async function fetchFoodSafetyDefinitions(
  venueId,
  { section, includeInactive = false, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const response = await apiGet(
    'api2',
    `${foodSafetyBase(venueId)}/definitions`,
    withFoodSafetyCapabilities({
      queryStringParameters: {
        ...(section ? { section } : {}),
        ...(includeInactive ? { includeInactive: 'true' } : {}),
      },
    }),
  )
  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load definitions')
  }
  return Array.isArray(response.data) ? response.data : []
}

export async function createFoodSafetyDefinition(
  venueId,
  body,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  try {
    const response = await apiPost(
      'api2',
      `${foodSafetyBase(venueId)}/definitions`,
      withFoodSafetyCapabilities({ body }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to create definition')
    }
    notifyFoodSafetyChanged()
    return response.data
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message
    const err = new Error(msg || 'Failed to create definition')
    err.response = error?.response
    throw err
  }
}

export async function updateFoodSafetyDefinition(
  venueId,
  definitionId,
  body,
  { apiPatch = API.patch } = {},
) {
  if (!venueId || !definitionId) {
    throw new Error('venueId and definitionId required')
  }
  try {
    const response = await apiPatch(
      'api2',
      `${foodSafetyBase(venueId)}/definitions/${definitionId}`,
      withFoodSafetyCapabilities({ body }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to update definition')
    }
    notifyFoodSafetyChanged()
    return response.data
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message
    const err = new Error(msg || 'Failed to update definition')
    err.response = error?.response
    throw err
  }
}

export async function archiveFoodSafetyDefinition(
  venueId,
  definitionId,
  { apiDelete = API.delete } = {},
) {
  if (!venueId || !definitionId) {
    throw new Error('venueId and definitionId required')
  }
  try {
    const response = await apiDelete(
      'api2',
      `${foodSafetyBase(venueId)}/definitions/${definitionId}`,
      withFoodSafetyCapabilities(),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to archive definition')
    }
    notifyFoodSafetyChanged()
    return response.data
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message
    const err = new Error(msg || 'Failed to archive definition')
    err.response = error?.response
    throw err
  }
}

export async function fetchFoodSafetySettings(
  venueId,
  { apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const response = await apiGet(
    'api2',
    `${foodSafetyBase(venueId)}/settings`,
    withFoodSafetyCapabilities(),
  )
  if (!response?.success) {
    throw new Error(response?.message || 'Failed to load settings')
  }
  return response.data
}

export async function updateFoodSafetySettings(
  venueId,
  body,
  { apiPatch = API.patch } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  try {
    const response = await apiPatch(
      'api2',
      `${foodSafetyBase(venueId)}/settings`,
      withFoodSafetyCapabilities({ body }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to save settings')
    }
    return response.data
  } catch (error) {
    const msg = error?.response?.data?.message || error?.message
    const err = new Error(msg || 'Failed to save settings')
    err.response = error?.response
    throw err
  }
}

/**
 * Week status grid (This Week / Last Week / explicit weekStart).
 * Free may 403 on fully-older weeks — callers show contextual Starter paywall.
 */
export async function fetchFoodSafetyWeekReport(
  venueId,
  { preset, weekStart, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!preset && !weekStart) {
    throw new Error('preset or weekStart is required')
  }

  try {
    const response = await apiGet(
      'api2',
      `${foodSafetyBase(venueId)}/reports/week`,
      withFoodSafetyCapabilities({
        queryStringParameters: {
          ...(preset ? { preset } : {}),
          ...(weekStart ? { weekStart } : {}),
        },
      }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to load Food Safety report')
    }
    return response.data
  } catch (error) {
    const lock = readFoodSafetyHistoryLock(error)
    if (lock.locked) {
      const err = new Error(lock.message)
      err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
      err.historyAccess = lock.data
      err.statusCode = 403
      throw err
    }
    throw error
  }
}

export function filenameFromContentDisposition(headerValue) {
  if (!headerValue) return ''
  const utf8Match = String(headerValue).match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
  }
  const fallbackMatch = String(headerValue).match(/filename="?([^";]+)"?/i)
  return fallbackMatch?.[1] || ''
}

async function readBlobErrorPayload(data) {
  if (!data) return null
  if (typeof data === 'object' && !(data instanceof Blob) && data.message) {
    return data
  }
  try {
    const text =
      typeof data?.text === 'function'
        ? await data.text()
        : typeof data === 'string'
        ? data
        : null
    if (!text) return null
    return JSON.parse(text)
  } catch {
    return null
  }
}

/**
 * Download Food Safety week PDF (Starter+). Free → history lock error.
 * Uses expressAPI blob response (not Amplify-style API wrapper).
 */
export async function downloadFoodSafetyWeekPdf(
  venueId,
  { preset, weekStart, from, to, httpGet = expressAPI.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!preset && !weekStart && !(from && to)) {
    throw new Error('preset, weekStart, or from/to is required')
  }

  try {
    const response = await httpGet(
      `venue/${venueId}/food-safety/reports/week/pdf`,
      withFoodSafetyCapabilities({
        params: {
          ...(preset ? { preset } : {}),
          ...(weekStart ? { weekStart } : {}),
          ...(from && to ? { from, to } : {}),
        },
        responseType: 'blob',
      }),
    )

    const filename =
      filenameFromContentDisposition(
        response.headers?.['content-disposition'],
      ) ||
      `Food_Safety_Report_${
        from && to ? `${from}_to_${to}` : weekStart || preset || 'week'
      }.pdf`

    const blob =
      response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data || new Uint8Array()], {
            type: 'application/pdf',
          })

    return { blob, filename, contentType: 'application/pdf' }
  } catch (error) {
    const parsed = await readBlobErrorPayload(error?.response?.data)
    if (
      parsed?.code !== FOOD_SAFETY_PERMISSION_DENIED_CODE &&
      (error?.response?.status === 403 ||
        parsed?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE)
    ) {
      const err = new Error(
        parsed?.message || 'Food Safety PDF download is available on Starter.',
      )
      err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
      err.historyAccess = parsed?.data || null
      err.statusCode = 403
      throw err
    }
    const msg = parsed?.message || error?.message || 'Failed to download PDF'
    const err = new Error(msg)
    err.statusCode = error?.response?.status || error?.statusCode
    err.code = parsed?.code || error?.code
    throw err
  }
}

/**
 * Free-safe SAMPLE PDF — fictional kitchen, no Starter gate.
 */
export async function downloadFoodSafetySamplePdf(
  venueId,
  { httpGet = expressAPI.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  try {
    const response = await httpGet(
      `venue/${venueId}/food-safety/reports/sample-pdf`,
      withFoodSafetyCapabilities({ responseType: 'blob' }),
    )
    const filename =
      filenameFromContentDisposition(
        response.headers?.['content-disposition'],
      ) || 'Food_Safety_SAMPLE_Report.pdf'
    const blob =
      response?.data instanceof Blob
        ? response.data
        : new Blob([response?.data || new Uint8Array()], {
            type: 'application/pdf',
          })
    return { blob, filename, contentType: 'application/pdf' }
  } catch (error) {
    const parsed = await readBlobErrorPayload(error?.response?.data)
    throw new Error(
      parsed?.message || error?.message || 'Failed to download sample PDF',
    )
  }
}

/**
 * Free-safe SAMPLE PDF email — fictional kitchen, no Starter gate.
 */
export async function emailFoodSafetySamplePdf(
  venueId,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  try {
    const response = await apiPost(
      'api2',
      `${foodSafetyBase(venueId)}/reports/sample-pdf/email`,
      withFoodSafetyCapabilities({ body: {} }),
    )
    if (!response?.success) {
      throw new Error(
        response?.message || 'Failed to email sample Food Safety report',
      )
    }
    return response.data
  } catch (error) {
    const data = error?.response?.data
    const msg =
      data?.message ||
      error?.message ||
      'Failed to email sample Food Safety report'
    const err = new Error(msg)
    err.statusCode = error?.response?.status || error?.statusCode
    err.response = error?.response
    throw err
  }
}

/**
 * Email Food Safety week PDF to the venue manager report email (Starter+).
 */
export async function emailFoodSafetyWeekPdf(
  venueId,
  { preset, weekStart, from, to, apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!preset && !weekStart && !(from && to)) {
    throw new Error('preset, weekStart, or from/to is required')
  }

  try {
    const response = await apiPost(
      'api2',
      `${foodSafetyBase(venueId)}/reports/week/pdf/email`,
      withFoodSafetyCapabilities({
        body: {
          ...(preset ? { preset } : {}),
          ...(weekStart ? { weekStart } : {}),
          ...(from && to ? { from, to } : {}),
        },
      }),
    )
    if (!response?.success) {
      throw new Error(response?.message || 'Failed to email Food Safety PDF')
    }
    return response.data
  } catch (error) {
    const data = error?.response?.data
    if (
      data?.code !== FOOD_SAFETY_PERMISSION_DENIED_CODE &&
      (error?.response?.status === 403 ||
        data?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE)
    ) {
      const err = new Error(
        data?.message || 'Food Safety PDF email is available on Starter.',
      )
      err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
      err.historyAccess = data?.data || null
      err.statusCode = 403
      throw err
    }
    const msg =
      data?.message || error?.message || 'Failed to email Food Safety PDF'
    const err = new Error(msg)
    err.statusCode = error?.response?.status || error?.statusCode
    err.code = data?.code || error?.code
    err.response = error?.response
    throw err
  }
}

/**
 * 403 when the venue role cannot complete / manage Food Safety. Express
 * names the role and how to change it in `message`.
 * @returns {{ denied: boolean, message: string, data: object|null }}
 */
export function readFoodSafetyPermissionDenied(error) {
  const data = error?.response?.data
  if (
    error?.response?.status === 403 &&
    data?.code === FOOD_SAFETY_PERMISSION_DENIED_CODE
  ) {
    return {
      denied: true,
      message: data.message || '',
      data: data.data || null,
    }
  }
  return { denied: false, message: '', data: null }
}

function toFoodSafetyRequestError(error, fallbackMessage) {
  const lock = readFoodSafetyHistoryLock(error)
  if (lock.locked) {
    const err = new Error(lock.message)
    err.code = FOOD_SAFETY_HISTORY_LOCKED_CODE
    err.historyAccess = lock.data
    err.statusCode = 403
    return err
  }
  const data = error?.response?.data
  const err = new Error(data?.message || error?.message || fallbackMessage)
  err.statusCode = error?.response?.status || error?.statusCode
  err.code = data?.code || error?.code
  err.response = error?.response
  return err
}

async function requestFoodSafety(
  request,
  fallbackMessage,
  { notify = false } = {},
) {
  let response
  try {
    response = await request()
  } catch (error) {
    throw toFoodSafetyRequestError(error, fallbackMessage)
  }
  if (!response?.success) {
    const err = new Error(response?.message || fallbackMessage)
    err.statusCode = 400
    throw err
  }
  if (notify) notifyFoodSafetyChanged()
  return response.data
}

/**
 * Exceptions (out-of-range readings, rejected deliveries, checklist issues…).
 * Express defaults to open; pass `status: 'all'` for both.
 * @param {{ status?: 'open'|'resolved'|'all', section?: string, limit?: number, apiGet?: Function }} [options]
 * @returns {Promise<{ exceptions: object[], openCount: number, limit: number|null }>}
 */
export async function fetchFoodSafetyExceptions(
  venueId,
  { status, section, limit, includeRecheckDue = false, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const data = await requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/exceptions`,
        withFoodSafetyCapabilities({
          queryStringParameters: {
            ...(status ? { status } : {}),
            ...(section ? { section } : {}),
            ...(limit ? { limit } : {}),
            ...(includeRecheckDue ? { includeRecheckDue: '1' } : {}),
          },
        }),
      ),
    'Failed to load Food Safety exceptions',
  )
  return {
    exceptions: Array.isArray(data?.exceptions) ? data.exceptions : [],
    openCount: Number(data?.openCount) || 0,
    limit: data?.limit ?? null,
    recheckDue: Array.isArray(data?.recheckDue) ? data.recheckDue : [],
  }
}

/**
 * Record what was done about an exception (resolves it; the original
 * reading is never changed).
 * @param {{ type: string, notes?: string }} correctiveAction
 */
export async function addFoodSafetyCorrectiveAction(
  venueId,
  exceptionId,
  correctiveAction,
  { apiPost = API.post } = {},
) {
  if (!venueId || !exceptionId) {
    throw new Error('venueId and exceptionId required')
  }
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(
          venueId,
        )}/exceptions/${exceptionId}/corrective-actions`,
        withFoodSafetyCapabilities({ body: correctiveAction || {} }),
      ),
    'Failed to save corrective action',
    { notify: true },
  )
}

/**
 * Optional follow-up reading after a corrective action.
 * @param {{ value: number, notes?: string }} recheck
 */
export async function recordFoodSafetyExceptionRecheck(
  venueId,
  exceptionId,
  recheck,
  { apiPost = API.post } = {},
) {
  if (!venueId || !exceptionId) {
    throw new Error('venueId and exceptionId required')
  }
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(venueId)}/exceptions/${exceptionId}/recheck`,
        withFoodSafetyCapabilities({ body: recheck || {} }),
      ),
    'Failed to save recheck',
    { notify: true },
  )
}

/**
 * Mark a fridge / freezer out of service (stops asking for readings).
 * @param {{ reason: string, from?: string }} body
 */
export async function markFoodSafetyDefinitionOutOfService(
  venueId,
  definitionId,
  body,
  { apiPost = API.post } = {},
) {
  if (!venueId || !definitionId) {
    throw new Error('venueId and definitionId required')
  }
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(venueId)}/definitions/${definitionId}/out-of-service`,
        withFoodSafetyCapabilities({ body: body || {} }),
      ),
    'Failed to mark out of service',
    { notify: true },
  )
}

/** @param {{ at?: string }} [body] */
export async function returnFoodSafetyDefinitionToService(
  venueId,
  definitionId,
  body,
  { apiPost = API.post } = {},
) {
  if (!venueId || !definitionId) {
    throw new Error('venueId and definitionId required')
  }
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(
          venueId,
        )}/definitions/${definitionId}/return-to-service`,
        withFoodSafetyCapabilities({ body: body || {} }),
      ),
    'Failed to return to service',
    { notify: true },
  )
}

/**
 * Recently receipted OMS orders for the Deliveries picker.
 * @param {{ days?: number, limit?: number, apiGet?: Function }} [options]
 * @returns {Promise<{ days: number|null, receipts: object[] }>}
 */
export async function fetchFoodSafetyRecentReceipts(
  venueId,
  { days, limit, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const data = await requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/receipts`,
        withFoodSafetyCapabilities({
          queryStringParameters: {
            ...(days ? { days } : {}),
            ...(limit ? { limit } : {}),
          },
        }),
      ),
    'Failed to load recent deliveries',
  )
  return {
    days: data?.days ?? null,
    receipts: Array.isArray(data?.receipts) ? data.receipts : [],
  }
}

/** One receipted order's lines, for pre-filling delivery checks. */
export async function fetchFoodSafetyReceipt(
  venueId,
  purchaseOrderNumber,
  { supplierId, apiGet = API.get } = {},
) {
  if (!venueId || !purchaseOrderNumber) {
    throw new Error('venueId and purchaseOrderNumber required')
  }
  return requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/receipts/${encodeURIComponent(
          purchaseOrderNumber,
        )}`,
        withFoodSafetyCapabilities({
          queryStringParameters: supplierId ? { supplierId } : undefined,
        }),
      ),
    'Failed to load delivery',
  )
}

/**
 * Submit delivery checks for several receipted products together.
 * @param {{ purchaseOrderNumber: string, supplierId?: string, localDate?: string, completedAt?: string, checks: object[] }} body
 */
export async function createFoodSafetyDeliveryBatch(
  venueId,
  body,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(venueId)}/deliveries/batch`,
        withFoodSafetyCapabilities({ body }),
      ),
    'Failed to save delivery checks',
    { notify: true },
  )
}

/** Setup readiness — each area's state plus the last confirmation. */
export async function fetchFoodSafetySetupReadiness(
  venueId,
  { apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  return requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/setup/readiness`,
        withFoodSafetyCapabilities(),
      ),
    'Failed to load Food Safety setup readiness',
  )
}

/** "Review & confirm Food Safety setup". */
export async function confirmFoodSafetySetupReadiness(
  venueId,
  { apiPost = API.post } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(venueId)}/setup/readiness/confirm`,
        withFoodSafetyCapabilities({ body: {} }),
      ),
    'Failed to confirm Food Safety setup',
  )
}

/** Report day detail (every check that day, with exceptions). */
export async function fetchFoodSafetyReportDay(
  venueId,
  { date, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  if (!date) throw new Error('date is required')
  return requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/reports/day`,
        withFoodSafetyCapabilities({ queryStringParameters: { date } }),
      ),
    'Failed to load Food Safety day report',
  )
}

/**
 * Setup change history — venue-wide (optionally since an ISO date), or one
 * definition's revisions when `definitionId` is given.
 */
export async function fetchFoodSafetyDefinitionRevisions(
  venueId,
  { definitionId, since, apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId is required')
  const path = definitionId
    ? `${foodSafetyBase(venueId)}/definitions/${definitionId}/revisions`
    : `${foodSafetyBase(venueId)}/definition-revisions`
  const data = await requestFoodSafety(
    () =>
      apiGet(
        'api2',
        path,
        withFoodSafetyCapabilities({
          queryStringParameters: !definitionId && since ? { since } : undefined,
        }),
      ),
    'Failed to load Food Safety setup history',
  )
  return Array.isArray(data) ? data : []
}

/**
 * Documents shelf.
 * @returns {Promise<{ documents: object[], canRemove: boolean }>}
 */
export async function fetchFoodSafetyDocuments(
  venueId,
  { apiGet = API.get } = {},
) {
  if (!venueId) throw new Error('venueId required')
  return requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/documents`,
        withFoodSafetyCapabilities(),
      ),
    'Could not load documents',
  )
}

/** @param {{ file: File, title?: string, category?: string }} upload */
export async function uploadFoodSafetyDocument(
  venueId,
  { file, title, category },
  { apiPost = API.post } = {},
) {
  if (!venueId || !file) throw new Error('venueId and file required')
  const body = new FormData()
  body.append('file', file, file.name)
  if (title) body.append('title', title)
  if (category) body.append('category', category)
  return requestFoodSafety(
    () =>
      apiPost(
        'api2',
        `${foodSafetyBase(venueId)}/documents`,
        withFoodSafetyCapabilities({ body, timeout: 120000 }),
      ),
    'Could not upload the document',
  )
}

/** @returns {Promise<{ url: string, fileName: string, contentType: string }>} */
export async function fetchFoodSafetyDocumentDownload(
  venueId,
  documentId,
  { apiGet = API.get } = {},
) {
  if (!venueId || !documentId) {
    throw new Error('venueId and documentId required')
  }
  return requestFoodSafety(
    () =>
      apiGet(
        'api2',
        `${foodSafetyBase(venueId)}/documents/${documentId}/download`,
        withFoodSafetyCapabilities(),
      ),
    'Could not open the document',
  )
}

export async function removeFoodSafetyDocument(
  venueId,
  documentId,
  { apiDelete = API.delete } = {},
) {
  if (!venueId || !documentId) {
    throw new Error('venueId and documentId required')
  }
  return requestFoodSafety(
    () =>
      apiDelete(
        'api2',
        `${foodSafetyBase(venueId)}/documents/${documentId}`,
        withFoodSafetyCapabilities(),
      ),
    'Could not remove the document',
  )
}

/** Venue training history. Reads use operational access; writes require manage. */
export function fetchStaffTraining(venueId) {
  return requestFoodSafety(
    () =>
      API.get(
        'api2',
        `${foodSafetyBase(venueId)}/training`,
        withFoodSafetyCapabilities(),
      ),
    'Could not load staff training',
  )
}

export function fetchEmployeeTraining(venueId, subjectKey) {
  return requestFoodSafety(
    () =>
      API.get(
        'api2',
        `${foodSafetyBase(venueId)}/training/employees/${encodeURIComponent(
          subjectKey,
        )}`,
        withFoodSafetyCapabilities(),
      ),
    'Could not load employee training',
  )
}

export function addTrainingEmployee(venueId, name) {
  return requestFoodSafety(
    () =>
      API.post(
        'api2',
        `${foodSafetyBase(venueId)}/training/employees`,
        withFoodSafetyCapabilities({ body: { name } }),
      ),
    'Could not add employee',
  )
}

export function saveTrainingTopic(venueId, body) {
  return requestFoodSafety(
    () =>
      API.post(
        'api2',
        `${foodSafetyBase(venueId)}/training/topics`,
        withFoodSafetyCapabilities({ body }),
      ),
    'Could not save training topic',
  )
}

export function saveTrainingRecord(venueId, body, recordId = null) {
  const path = `${foodSafetyBase(venueId)}/training/records${
    recordId ? `/${recordId}` : ''
  }`
  return requestFoodSafety(
    () =>
      API[recordId ? 'patch' : 'post'](
        'api2',
        path,
        withFoodSafetyCapabilities({ body }),
      ),
    'Could not save training record',
  )
}

export function uploadTrainingEvidence(venueId, recordId, revision, file) {
  const body = new FormData()
  body.append('file', file, file.name)
  body.append('revision', String(revision))
  return requestFoodSafety(
    () =>
      API.post(
        'api2',
        `${foodSafetyBase(venueId)}/training/records/${recordId}/evidence`,
        withFoodSafetyCapabilities({ body, timeout: 120000 }),
      ),
    'Could not upload training evidence',
  )
}

export function fetchFoodSafetyVisitors(venueId, filters = {}) {
  return requestFoodSafety(
    () =>
      API.get(
        'api2',
        `${foodSafetyBase(venueId)}/visitors`,
        withFoodSafetyCapabilities({ queryStringParameters: filters }),
      ),
    'Could not load the visitor log',
  )
}

export function saveFoodSafetyVisitorPolicy(venueId, body) {
  return requestFoodSafety(
    () =>
      API.patch(
        'api2',
        `${foodSafetyBase(venueId)}/visitors/policy`,
        withFoodSafetyCapabilities({ body }),
      ),
    'Could not save visitor settings',
  )
}

export function signInFoodSafetyVisitor(venueId, body) {
  return requestFoodSafety(
    () =>
      API.post(
        'api2',
        `${foodSafetyBase(venueId)}/visitors`,
        withFoodSafetyCapabilities({ body }),
      ),
    'Could not record sign-in',
  )
}

export function signOutFoodSafetyVisitor(venueId, visitId) {
  return requestFoodSafety(
    () =>
      API.post(
        'api2',
        `${foodSafetyBase(venueId)}/visitors/${visitId}/sign-out`,
        withFoodSafetyCapabilities({ body: {} }),
      ),
    'Could not record departure',
  )
}

export function fetchFoodSafetyVisitorDeclaration(venueId, visitId) {
  return requestFoodSafety(
    () =>
      API.get(
        'api2',
        `${foodSafetyBase(venueId)}/visitors/${visitId}/declaration`,
        withFoodSafetyCapabilities(),
      ),
    'Could not open the declaration',
  )
}
